/**
 * Shift Tic Tac Toe - AI Engine
 * Implements Easy, Medium, and Hard difficulty levels
 */

import {
  placePiece,
  shiftPiece,
  getValidPlacements,
  getValidShifts,
  getOpponent,
} from './gameLogic';

import {
  findWinningPlacement,
  findWinningShift,
  findBlockingPlacement,
  findBlockingShift,
  analyzePosition,
  wouldWin,
  shiftWouldWin,
} from './moveValidator';

// Position values for strategic evaluation
const POSITION_VALUES = {
  center: 4,
  corners: [0, 2, 6, 8],
  edges: [1, 3, 5, 7],
};

/**
 * Get AI move based on difficulty
 * @param {Object} state - Current game state
 * @param {string} difficulty - 'easy' | 'medium' | 'hard'
 * @param {string} aiPlayer - 'X' or 'O'
 * @returns {Object|null} Move object { type, position } or { type, from, to }
 */
export function getAIMove(state, difficulty, aiPlayer = 'O') {
  if (state.winner || state.isDraw) return null;
  if (state.currentPlayer !== aiPlayer) return null;
  
  switch (difficulty) {
    case 'easy':
      return getEasyMove(state, aiPlayer);
    case 'medium':
      return getMediumMove(state, aiPlayer);
    case 'hard':
      return getHardMove(state, aiPlayer);
    default:
      return getMediumMove(state, aiPlayer);
  }
}

/**
 * Easy AI - Random valid moves
 * @param {Object} state - Current game state
 * @param {string} aiPlayer - AI's piece
 * @returns {Object|null} Move object
 */
export function getEasyMove(state, aiPlayer) {
  const { phase } = state;
  
  if (phase === 'placement') {
    const validPositions = getValidPlacements(state.board);
    if (validPositions.length === 0) return null;
    
    const randomIndex = Math.floor(Math.random() * validPositions.length);
    return { type: 'place', position: validPositions[randomIndex] };
  }
  
  if (phase === 'shift') {
    const validShifts = getValidShifts(state);
    if (validShifts.length === 0) return null;
    
    const randomIndex = Math.floor(Math.random() * validShifts.length);
    const { from, to } = validShifts[randomIndex];
    return { type: 'shift', from, to };
  }
  
  return null;
}

/**
 * Medium AI - Priority-based strategy
 * Priorities: Win > Block > Center > Corners > Edges > Random
 * @param {Object} state - Current game state
 * @param {string} aiPlayer - AI's piece
 * @returns {Object|null} Move object
 */
export function getMediumMove(state, aiPlayer) {
  const { phase, board } = state;
  const opponent = getOpponent(aiPlayer);
  
  if (phase === 'placement') {
    // 1. Try to win
    const winningPos = findWinningPlacement(board, aiPlayer);
    if (winningPos !== null) {
      return { type: 'place', position: winningPos };
    }
    
    // 2. Block opponent's win
    const blockingPos = findBlockingPlacement(board, opponent);
    if (blockingPos !== null) {
      return { type: 'place', position: blockingPos };
    }
    
    // 3. Take center if available
    if (board[POSITION_VALUES.center] === null) {
      return { type: 'place', position: POSITION_VALUES.center };
    }
    
    // 4. Take a corner
    const availableCorners = POSITION_VALUES.corners.filter(p => board[p] === null);
    if (availableCorners.length > 0) {
      const randomCorner = availableCorners[Math.floor(Math.random() * availableCorners.length)];
      return { type: 'place', position: randomCorner };
    }
    
    // 5. Take an edge
    const availableEdges = POSITION_VALUES.edges.filter(p => board[p] === null);
    if (availableEdges.length > 0) {
      const randomEdge = availableEdges[Math.floor(Math.random() * availableEdges.length)];
      return { type: 'place', position: randomEdge };
    }
    
    // 6. Random fallback
    return getEasyMove(state, aiPlayer);
  }
  
  if (phase === 'shift') {
    // 1. Try to win
    const winningShift = findWinningShift(state, aiPlayer);
    if (winningShift) {
      return { type: 'shift', ...winningShift };
    }
    
    // 2. Block opponent's winning shift
    const blockingShift = findBlockingShift(state, aiPlayer);
    if (blockingShift) {
      return { type: 'shift', ...blockingShift };
    }
    
    // 3. Move toward center if possible
    const validShifts = getValidShifts(state);
    const centerMoves = validShifts.filter(s => s.to === POSITION_VALUES.center);
    if (centerMoves.length > 0) {
      return { type: 'shift', ...centerMoves[0] };
    }
    
    // 4. Move to create two-in-a-row opportunities
    const twoInRowShifts = validShifts.filter(({ from, to }) => {
      const piece = board[from];
      const testBoard = [...board];
      testBoard[from] = null;
      testBoard[to] = piece;
      const analysis = analyzePosition(testBoard, aiPlayer);
      return analysis.twoInRow > 0;
    });
    if (twoInRowShifts.length > 0) {
      return { type: 'shift', ...twoInRowShifts[0] };
    }
    
    // 5. Random valid shift
    return getEasyMove(state, aiPlayer);
  }
  
  return null;
}

/**
 * Hard AI - Minimax with alpha-beta pruning
 * @param {Object} state - Current game state
 * @param {string} aiPlayer - AI's piece
 * @returns {Object|null} Move object
 */
export function getHardMove(state, aiPlayer) {
  const { phase } = state;
  
  // Use minimax to find the best move
  const result = minimax(state, aiPlayer, aiPlayer, 6, -Infinity, Infinity, true);
  
  if (result.move) {
    return result.move;
  }
  
  // Fallback to medium strategy
  return getMediumMove(state, aiPlayer);
}

/**
 * Minimax algorithm with alpha-beta pruning
 * @param {Object} state - Current game state
 * @param {string} aiPlayer - The AI player
 * @param {string} currentPlayer - Current player in simulation
 * @param {number} depth - Search depth
 * @param {number} alpha - Alpha value for pruning
 * @param {number} beta - Beta value for pruning
 * @param {boolean} isMaximizing - Is maximizing player
 * @returns {Object} { score, move }
 */
function minimax(state, aiPlayer, currentPlayer, depth, alpha, beta, isMaximizing) {
  // Terminal conditions
  if (state.winner) {
    return { score: state.winner === aiPlayer ? 100 + depth : -100 - depth, move: null };
  }
  if (state.isDraw) {
    return { score: 0, move: null };
  }
  if (depth === 0) {
    return { score: evaluatePosition(state, aiPlayer), move: null };
  }
  
  const { phase } = state;
  const opponent = getOpponent(currentPlayer);
  
  if (isMaximizing) {
    let bestScore = -Infinity;
    let bestMove = null;
    
    const moves = getMovesForPlayer(state, currentPlayer);
    
    for (const move of moves) {
      const newState = applyMove(state, move, currentPlayer);
      if (newState.error) continue;
      
      const result = minimax(
        newState,
        aiPlayer,
        newState.currentPlayer,
        depth - 1,
        alpha,
        beta,
        false
      );
      
      if (result.score > bestScore) {
        bestScore = result.score;
        bestMove = move;
      }
      
      alpha = Math.max(alpha, result.score);
      if (beta <= alpha) break; // Pruning
    }
    
    return { score: bestScore, move: bestMove };
  } else {
    let bestScore = Infinity;
    let bestMove = null;
    
    const moves = getMovesForPlayer(state, currentPlayer);
    
    for (const move of moves) {
      const newState = applyMove(state, move, currentPlayer);
      if (newState.error) continue;
      
      const result = minimax(
        newState,
        aiPlayer,
        newState.currentPlayer,
        depth - 1,
        alpha,
        beta,
        true
      );
      
      if (result.score < bestScore) {
        bestScore = result.score;
        bestMove = move;
      }
      
      beta = Math.min(beta, result.score);
      if (beta <= alpha) break; // Pruning
    }
    
    return { score: bestScore, move: bestMove };
  }
}

/**
 * Apply a move to create new state
 * @param {Object} state - Current state
 * @param {Object} move - Move to apply
 * @param {string} player - Player making move
 * @returns {Object} New state
 */
function getMovesForPlayer(state, player) {
  if (state.phase === 'placement') {
    return getValidPlacements(state.board).map((p) => ({
      type: 'place',
      position: p,
    }));
  }

  return getValidShifts(state).map((s) => ({
    type: 'shift',
    from: s.from,
    to: s.to,
  }));
}

function applyMove(state, move, player) {
  if (move.type === 'place') {
    return placePiece(state, move.position, player);
  }
  if (move.type === 'shift') {
    return shiftPiece(state, move.from, move.to, player);
  }
  return state;
}

/**
 * Evaluate board position for scoring
 * @param {Object} state - Current game state
 * @param {string} aiPlayer - AI's piece
 * @returns {number} Position score
 */
function evaluatePosition(state, aiPlayer) {
  const { board } = state;
  const opponent = getOpponent(aiPlayer);
  
  let score = 0;
  
  // Analyze AI's position
  const aiAnalysis = analyzePosition(board, aiPlayer);
  score += aiAnalysis.twoInRow * 10;
  
  // Analyze opponent's position (negative)
  const oppAnalysis = analyzePosition(board, opponent);
  score -= oppAnalysis.twoInRow * 10;
  
  // Bonus for center control
  if (board[POSITION_VALUES.center] === aiPlayer) score += 3;
  if (board[POSITION_VALUES.center] === opponent) score -= 3;
  
  // Bonus for corner control
  for (const corner of POSITION_VALUES.corners) {
    if (board[corner] === aiPlayer) score += 2;
    if (board[corner] === opponent) score -= 2;
  }
  
  return score;
}

/**
 * Get AI move with delay (for natural feeling)
 * @param {Object} state - Current game state
 * @param {string} difficulty - Difficulty level
 * @param {string} aiPlayer - AI's piece
 * @param {number} delayMs - Delay in milliseconds
 * @returns {Promise<Object|null>} Move object
 */
export async function getAIMoveWithDelay(state, difficulty, aiPlayer = 'O', delayMs = 500) {
  await new Promise(resolve => setTimeout(resolve, delayMs));
  return getAIMove(state, difficulty, aiPlayer);
}

/**
 * Get difficulty description
 * @param {string} difficulty - Difficulty level
 * @returns {string} Description
 */
export function getDifficultyDescription(difficulty) {
  switch (difficulty) {
    case 'easy':
      return 'Random moves - good for learning';
    case 'medium':
      return 'Strategic play - blocks and attacks';
    case 'hard':
      return 'Expert AI - very difficult to beat';
    default:
      return 'Unknown difficulty';
  }
}

export default {
  getAIMove,
  getAIMoveWithDelay,
  getEasyMove,
  getMediumMove,
  getHardMove,
  getDifficultyDescription,
};
